import React, { type FC } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { keyframes, styled } from 'styled-components';

import { Button } from '@dutchie-pos/components';
import ChevronLeft from 'src/assets/chevron-left.svg?react';
import GenericBank from 'src/assets/generic-bank.svg?react';
import { WaitForConfirmationIllustration } from 'src/assets/wait-for-confirmation';

type Props = {
  instoreCartUrl: string | null;
  isLoading: boolean;
  isConfirming: boolean;
  error: Error | null;
  onBack: () => void;
  onRetry: () => void;
};

export const PayByBankInstoreQrScreen: FC<Props> = ({
  instoreCartUrl,
  isLoading,
  isConfirming,
  error,
  onBack,
  onRetry,
}) => {
  const renderContent = () => {
    if (error) {
      return (
        <ErrorState>
          <ErrorMessage>Failed to generate Pay By Bank link. Please try again.</ErrorMessage>
          <Button label='Retry' onClick={onRetry} />
        </ErrorState>
      );
    }

    if (isConfirming) {
      return (
        <ConfirmingState>
          <WaitForConfirmationIllustration />
          <ConfirmingTitle>Wait for customer to confirm order on their phone</ConfirmingTitle>
          <ConfirmingSubtext>The customer must confirm the order on their phone.</ConfirmingSubtext>
        </ConfirmingState>
      );
    }

    if (isLoading || !instoreCartUrl) {
      return (
        <LoadingState>
          <Spinner />
          <LoadingText>Generating Pay By Bank link…</LoadingText>
        </LoadingState>
      );
    }

    return (
      <QrState>
        <Instruction>Customer must scan QR code below and complete payment on their phone.</Instruction>
        <QrCard>
          <QrCodeWrapper>
            <QRCodeSVG data-testid='pbb-instore-qr-code' value={instoreCartUrl} size={260} level='M' />
            <QrCenterIcon>
              <GenericBank width={24} height={24} />
            </QrCenterIcon>
          </QrCodeWrapper>
          <ScanLabel>Scan to pay by bank</ScanLabel>
        </QrCard>
      </QrState>
    );
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={onBack} aria-label='Back'>
          <ChevronLeft />
        </BackButton>
        <HeaderTitle>Pay by Bank</HeaderTitle>
      </Header>
      {renderContent()}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 0.5rem 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  border: 1.5px solid var(--color-gray-30);
  background: #fff;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const HeaderTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-gray-90);
  margin: 0;
`;

const Instruction = styled.p`
  font-size: 0.9375rem;
  color: var(--color-gray-50);
  text-align: center;
  line-height: 1.5;
  margin: 0;
`;

const QrState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
`;

const QrCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  background: #fff;
  border: 1.5px solid var(--color-gray-20);
  border-radius: 1rem;
  padding: 1.5rem;
  width: 100%;
`;

const QrCodeWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const QrCenterIcon = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-blue-60);
  border-radius: 0.375rem;
  padding: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;

  svg path {
    fill: #fff;
  }
`;

const ScanLabel = styled.p`
  font-size: 0.875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-gray-90);
  margin: 0;
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 3rem 0;
`;

const LoadingText = styled.p`
  font-size: 0.875rem;
  color: var(--color-gray-50);
  margin: 0;
`;

const ConfirmingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  text-align: center;
`;

const ConfirmingTitle = styled.p`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--color-gray-90);
  margin: 0;
  line-height: 1.4;
`;

const ConfirmingSubtext = styled.p`
  font-size: 0.9375rem;
  color: var(--color-gray-50);
  margin: 0;
  line-height: 1.5;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 3rem 0;
`;

const ErrorMessage = styled.p`
  font-size: 0.875rem;
  color: var(--color-red-50);
  text-align: center;
  margin: 0;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 3rem;
  height: 3rem;
  border: 3px solid var(--color-gray-20);
  border-top-color: var(--color-blue-60);
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;
